import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Megaphone, Plus, Trash, Eye, CursorClick, ChartLineUp,
  CaretDown, Play, Pause, CreditCard, Star, Crown, Check
} from '@phosphor-icons/react';

const PLACEMENT_TYPES = [
  { value: 'featured_tile', label: 'Featured Tile' },
  { value: 'promoted_search', label: 'Promoted Search' },
  { value: 'in_feed', label: 'In-Feed' },
  { value: 'homepage_banner', label: 'Homepage Banner' },
  { value: 'category_sponsor', label: 'Category Sponsor' },
  { value: 'live_sponsor', label: 'Live Sponsor' },
];

export default function SponsoredPlacements() {
  const { user } = useAuth();
  const isAdvertiser = user?.role === 'advertiser' || user?.role === 'admin';
  const [placements, setPlacements] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [plans, setPlans] = useState([]);
  const [mySub, setMySub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [tab, setTab] = useState(isAdvertiser ? 'placements' : 'plans');
  const [form, setForm] = useState({
    type: 'featured_tile', pricing_model: 'CPC', title: '', description: '',
    destination_url: '', offer_id: '', bid_amount: 0.5, daily_budget: 50, total_budget: 500,
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const promises = [api.get('/sponsored/plans'), api.get('/sponsored/subscription')];
      if (isAdvertiser) {
        promises.push(api.get('/sponsored/placements'), api.get('/sponsored/analytics'));
      }
      const results = await Promise.all(promises);
      setPlans(results[0].data || []);
      setMySub(results[1].data);
      if (isAdvertiser) {
        setPlacements(results[2].data || []);
        setAnalytics(results[3].data);
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [isAdvertiser]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const createPlacement = async () => {
    if (!form.title) return;
    try {
      await api.post('/sponsored/placements', form);
      toast.success('Placement created');
      setShowCreate(false);
      setForm({ type: 'featured_tile', pricing_model: 'CPC', title: '', description: '', destination_url: '', offer_id: '', bid_amount: 0.5, daily_budget: 50, total_budget: 500 });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/sponsored/placements/${id}`, { status });
      toast.success(`Placement ${status}`);
      fetchData();
    } catch (err) { toast.error('Failed'); }
  };

  const deletePlacement = async (id) => {
    try {
      await api.delete(`/sponsored/placements/${id}`);
      toast.success('Deleted');
      fetchData();
    } catch (err) { toast.error('Failed'); }
  };

  const subscribe = async (planId) => {
    try {
      await api.post('/sponsored/subscribe', { plan_id: planId, billing: 'monthly' });
      toast.success('Subscribed!');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const cancelSub = async () => {
    try {
      await api.post('/sponsored/subscription/cancel');
      toast.success('Subscription cancelled');
      fetchData();
    } catch (err) { toast.error('Failed'); }
  };

  const STATUS_STYLE = { draft: 'text-zinc-400 border-zinc-600', active: 'text-emerald-400 border-emerald-700/40', paused: 'text-amber-400 border-amber-700/40', completed: 'text-blue-400 border-blue-700/40' };
  const TIER_ICONS = { basic: Star, pro: Crown, enterprise: Crown };

  return (
    <div className="space-y-6" data-testid="sponsored-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{isAdvertiser ? 'Sponsored Placements' : 'Premium Plans'}</h1>
          <p className="text-sm text-zinc-500 mt-1">{isAdvertiser ? 'Create and manage sponsored campaigns' : 'Upgrade your creator toolkit'}</p>
        </div>
        {isAdvertiser && (
          <Button onClick={() => setShowCreate(true)} data-testid="create-placement-btn" className="bg-blue-600 hover:bg-blue-700"><Plus size={16} className="mr-2" /> New Placement</Button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-zinc-800 pb-px">
        {isAdvertiser && (
          <button onClick={() => setTab('placements')} className={`px-4 py-2 text-sm border-b-2 transition-colors ${tab === 'placements' ? 'border-blue-500 text-blue-400' : 'border-transparent text-zinc-500 hover:text-zinc-300'}`}>
            <Megaphone size={16} className="inline mr-1.5" />Placements
          </button>
        )}
        <button onClick={() => setTab('plans')} className={`px-4 py-2 text-sm border-b-2 transition-colors ${tab === 'plans' ? 'border-blue-500 text-blue-400' : 'border-transparent text-zinc-500 hover:text-zinc-300'}`}>
          <CreditCard size={16} className="inline mr-1.5" />Premium Plans
        </button>
      </div>

      {/* Analytics */}
      {tab === 'placements' && analytics && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          {[
            { label: 'Placements', value: analytics.total_placements },
            { label: 'Active', value: analytics.active },
            { label: 'Spend', value: `$${analytics.total_spend}` },
            { label: 'Impressions', value: analytics.total_impressions?.toLocaleString() },
            { label: 'Clicks', value: analytics.total_clicks?.toLocaleString() },
            { label: 'CTR', value: `${analytics.ctr}%` },
          ].map((s, i) => (
            <Card key={i} className="bg-zinc-900/50 border-zinc-800">
              <CardContent className="p-3">
                <p className="text-[10px] text-zinc-500 uppercase tracking-wider">{s.label}</p>
                <p className="text-lg font-bold mt-0.5">{s.value || 0}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Placement */}
      {showCreate && (
        <Card className="bg-zinc-900 border-zinc-700">
          <CardHeader className="pb-3"><CardTitle className="text-base">Create Sponsored Placement</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="relative">
                <select data-testid="placement-type" value={form.type} onChange={(e) => setForm(p => ({...p, type: e.target.value}))} className="w-full h-9 bg-zinc-800 border border-zinc-700 rounded-md px-3 text-sm text-zinc-300 appearance-none cursor-pointer">
                  {PLACEMENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
                <CaretDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
              </div>
              <div className="relative">
                <select value={form.pricing_model} onChange={(e) => setForm(p => ({...p, pricing_model: e.target.value}))} className="w-full h-9 bg-zinc-800 border border-zinc-700 rounded-md px-3 text-sm text-zinc-300 appearance-none cursor-pointer">
                  <option value="CPC">CPC</option><option value="CPM">CPM</option><option value="tenancy">Tenancy</option>
                </select>
                <CaretDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
              </div>
            </div>
            <Input data-testid="placement-title" value={form.title} onChange={(e) => setForm(p => ({...p, title: e.target.value}))} placeholder="Campaign title" className="bg-zinc-800 border-zinc-700" />
            <Input value={form.description} onChange={(e) => setForm(p => ({...p, description: e.target.value}))} placeholder="Description" className="bg-zinc-800 border-zinc-700" />
            <Input value={form.destination_url} onChange={(e) => setForm(p => ({...p, destination_url: e.target.value}))} placeholder="Destination URL" className="bg-zinc-800 border-zinc-700" />
            <div className="grid grid-cols-3 gap-3">
              <div><label className="text-xs text-zinc-500 mb-1 block">Bid</label><Input type="number" step="0.01" value={form.bid_amount} onChange={(e) => setForm(p => ({...p, bid_amount: parseFloat(e.target.value) || 0}))} className="bg-zinc-800 border-zinc-700" /></div>
              <div><label className="text-xs text-zinc-500 mb-1 block">Daily Budget</label><Input type="number" value={form.daily_budget} onChange={(e) => setForm(p => ({...p, daily_budget: parseFloat(e.target.value) || 0}))} className="bg-zinc-800 border-zinc-700" /></div>
              <div><label className="text-xs text-zinc-500 mb-1 block">Total Budget</label><Input type="number" value={form.total_budget} onChange={(e) => setForm(p => ({...p, total_budget: parseFloat(e.target.value) || 0}))} className="bg-zinc-800 border-zinc-700" /></div>
            </div>
            <div className="flex gap-2">
              <Button onClick={createPlacement} data-testid="save-placement-btn" className="bg-blue-600 hover:bg-blue-700">Create</Button>
              <Button variant="ghost" onClick={() => setShowCreate(false)} className="text-zinc-400">Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Placements List */}
      {tab === 'placements' && (
        <div>
          {loading ? <div className="text-center text-zinc-500 py-8">Loading...</div> : placements.length === 0 ? (
            <Card className="bg-zinc-900/50 border-zinc-800"><CardContent className="p-8 text-center"><Megaphone size={40} className="mx-auto text-zinc-600 mb-2" /><p className="text-zinc-500">No placements yet.</p></CardContent></Card>
          ) : (
            <div className="space-y-2">
              {placements.map(pl => (
                <div key={pl.placement_id} className="flex items-center justify-between p-3 rounded-lg bg-zinc-800/30 border border-zinc-800" data-testid={`placement-${pl.placement_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-zinc-200 truncate">{pl.title}</p>
                      <Badge variant="outline" className={`text-[10px] ${STATUS_STYLE[pl.status] || ''}`}>{pl.status}</Badge>
                      <Badge variant="outline" className="text-[10px] text-zinc-500 border-zinc-700">{pl.type?.replace('_', ' ')}</Badge>
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-[10px] text-zinc-500">
                      <span>{pl.pricing_model} ${pl.bid_amount}</span>
                      <span><Eye size={10} className="inline mr-0.5" />{pl.impressions || 0}</span>
                      <span><CursorClick size={10} className="inline mr-0.5" />{pl.clicks || 0}</span>
                      <span>Spent: ${(pl.spent || 0).toFixed(2)} / ${pl.total_budget}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0 ml-2">
                    {pl.status === 'draft' && <Button size="sm" variant="ghost" onClick={() => updateStatus(pl.placement_id, 'active')} className="h-7 px-2 text-xs text-emerald-400"><Play size={12} weight="fill" /></Button>}
                    {pl.status === 'active' && <Button size="sm" variant="ghost" onClick={() => updateStatus(pl.placement_id, 'paused')} className="h-7 px-2 text-xs text-amber-400"><Pause size={12} weight="fill" /></Button>}
                    {pl.status === 'paused' && <Button size="sm" variant="ghost" onClick={() => updateStatus(pl.placement_id, 'active')} className="h-7 px-2 text-xs text-emerald-400"><Play size={12} weight="fill" /></Button>}
                    <Button size="sm" variant="ghost" onClick={() => deletePlacement(pl.placement_id)} className="h-7 px-2 text-red-400 hover:text-red-300 hover:bg-red-900/20"><Trash size={12} /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Premium Plans */}
      {tab === 'plans' && (
        <div className="space-y-6">
          {mySub?.active && (
            <Card className="bg-zinc-900/50 border-emerald-800/30">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-emerald-400">Active: {mySub.plan_name}</p>
                    <p className="text-xs text-zinc-500">${mySub.price}/{mySub.billing} &middot; Since {new Date(mySub.started_at).toLocaleDateString()}</p>
                  </div>
                  <Button size="sm" variant="outline" onClick={cancelSub} className="border-red-700 text-red-400 text-xs">Cancel</Button>
                </div>
              </CardContent>
            </Card>
          )}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {plans.map(plan => {
              const TierIcon = TIER_ICONS[plan.tier] || Star;
              const isActive = mySub?.plan_id === plan.plan_id && mySub?.active;
              return (
                <Card key={plan.plan_id} className={`bg-zinc-900/50 border-zinc-800 ${plan.tier === 'pro' ? 'ring-1 ring-blue-500/30' : ''} ${isActive ? 'border-emerald-700/40' : ''}`} data-testid={`plan-${plan.plan_id}`}>
                  <CardContent className="p-5 space-y-4">
                    <div className="flex items-center gap-2">
                      <TierIcon size={20} weight="duotone" className={plan.tier === 'pro' ? 'text-blue-400' : plan.tier === 'enterprise' ? 'text-amber-400' : 'text-zinc-400'} />
                      <h3 className="font-bold">{plan.name}</h3>
                      {plan.tier === 'pro' && <Badge className="bg-blue-600/20 text-blue-400 text-[10px]">Popular</Badge>}
                    </div>
                    <div>
                      <span className="text-3xl font-bold">${plan.price_monthly}</span>
                      <span className="text-sm text-zinc-500">/mo</span>
                      <p className="text-xs text-zinc-600 mt-0.5">${plan.price_yearly}/yr (save {Math.round((1 - plan.price_yearly / (plan.price_monthly * 12)) * 100)}%)</p>
                    </div>
                    <ul className="space-y-1.5">
                      {plan.features.map((f, i) => (
                        <li key={i} className="flex items-start gap-2 text-xs text-zinc-400">
                          <Check size={14} className="text-emerald-400 mt-0.5 shrink-0" /> {f}
                        </li>
                      ))}
                    </ul>
                    {isActive ? (
                      <Button disabled className="w-full bg-emerald-900/30 text-emerald-400 cursor-default">Current Plan</Button>
                    ) : (
                      <Button onClick={() => subscribe(plan.plan_id)} data-testid={`subscribe-${plan.plan_id}`}
                        className={`w-full ${plan.tier === 'pro' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-zinc-700 hover:bg-zinc-600'}`}>
                        Subscribe
                      </Button>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
